/**
 * One PATCH of the open draft. The wizard queues callers so Send for review
 * never inherits a quiet autosave failure and then claims an unseen error.
 */
import type { ApiResult } from "@app/shared/api";
import { draftPayload, type DraftFormState } from "./draftForm";

export type DraftSaveResult =
  | { ok: true; recordVersion: number }
  | { ok: false; message: string };

export function draftSaveError(form: DraftFormState): string | null {
  const body = draftPayload(form);
  if (body) return null;
  return "Finish the rate, cap and dates before saving. The preview cannot send until those fields parse.";
}

export async function saveDraftOnce(input: {
  form: DraftFormState;
  recordVersion: number;
  patch: (body: Record<string, unknown>) => Promise<ApiResult<{ recordVersion?: number }>>;
  refreshVersion: () => Promise<number | null>;
}): Promise<DraftSaveResult> {
  const body = draftPayload(input.form);
  if (!body) return { ok: false, message: draftSaveError(input.form)! };

  let expected = input.recordVersion;
  for (let attempt = 0; attempt < 2; attempt++) {
    const r = await input.patch({ record_version: expected, ...body });
    if (r.ok) {
      const next = Number(r.data.recordVersion);
      return {
        ok: true,
        recordVersion: Number.isInteger(next) && next > 0 ? next : expected,
      };
    }
    if (r.code === "VERSION_CONFLICT" && attempt === 0) {
      const fresh = await input.refreshVersion();
      if (fresh != null && Number.isInteger(fresh) && fresh > 0) expected = fresh;
      continue;
    }
    const fields = r.fields
      ? Object.values(r.fields).filter((m) => m.trim()).join(" ")
      : "";
    return { ok: false, message: fields ? `${r.message} ${fields}`.trim() : r.message };
  }
  return { ok: false, message: "Someone else saved this campaign while you were editing. Try again." };
}
